import {
    generatePayloadMessage,
    getEventParameters,
    getSourceIframeName,
    getTargetIframe,
    isValidEvent,
} from "../utils/utils";

class EventManager {
    private static instance: EventManager;
    private requestIdToSourceMap: Map<string, Window> = new Map();
    private listenerSet: Set<(...args) => void> = new Set();

    private constructor() {
        // Register listener on object creation
        window.addEventListener("message", this.handleFrameEvent.bind(this));
    }

    public static getInstance(): EventManager {
        if (!EventManager.instance) {
            EventManager.instance = new EventManager();
        }

        return EventManager.instance;
    }

    /**
     * Handles FrameEvents posted to this window
     * @param event
     * @returns
     */
    private handleFrameEvent(event) {
        if (!isValidEvent(event)) {
            return;
        }

        const { type, data, requestId, source } = getEventParameters(event);

        // store this request id, we would use this to respond
        if (requestId) {
            this.requestIdToSourceMap.set(requestId, source);
        }

        // trigger listeners
        const sourceIframeName = getSourceIframeName(source);
        this.listenerSet.forEach((listener) => {
            listener(type, data, requestId, sourceIframeName);
        });
    }

    /**
     * Add a Listener
     * @param callback
     * @returns
     */
    public subscribe(callback) {
        this.listenerSet.add(callback);

        // unsubscribe callback
        return () => {
            this.listenerSet.delete(callback);
        };
    }

    /**
     * Sends a message to specific frame
     * @param frame
     * @param message
     */
    private sendMessageToFrame(frame: Window, message) {
        // For development the UI run on two different ports, hence a condition to check if its localhost then we use *
        const targetOrigin = window.location?.origin?.includes("localhost")
            ? "*"
            : window.location?.origin;
        frame.postMessage(generatePayloadMessage(message), targetOrigin);
    }

    /**
     * To be used for sending back a result for a specific requestId (for Request-Response Model)
     * @param requestId
     * @param message
     */
    public respondToRequest(requestId: string, message) {
        const frame = this.requestIdToSourceMap.get(requestId);
        if (frame) {
            this.sendMessageToFrame(frame, { requestId, ...message });
            this.requestIdToSourceMap.delete(requestId);
        } else {
            console.error(`requestId ${requestId} not found`);
        }
    }

    /**
     * To be used for sending a message to a specific frame (for PUSH events)
     * @param frameName
     * @param message
     */
    public sendMessage(frameName: string, message) {
        const frame = getTargetIframe(frameName);
        if (frame) {
            this.sendMessageToFrame(frame.contentWindow, message);
        } else {
            throw new Error(`frame ${frameName} not found`);
        }
    }
}

export default EventManager;
